'use client';

import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { MarketingCTA } from '@/components/marketing';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';

/**
 * Client component for pricing CTA section that handles authentication-aware CTA.
 *
 * This component wraps the MarketingCTA with authentication-aware button handling.
 * Since MarketingCTA supports onClick handlers, this client component provides
 * the interactive behavior while the static content is rendered server-side.
 *
 * @example
 * ```tsx
 * <PricingCTASection />
 * ```
 *
 * @see {@link MarketingCTA} - Base CTA component with loading state support
 * @see {@link PricingCTAButton} - Standalone button component for pricing cards
 */
export function PricingCTASection() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const handleGetStarted = async () => {
    if (!user) {
      router.push('/login');
      return;
    }

    setIsLoading(true);
    router.push('/checkout?plan=monthly');
  };

  return (
    <MarketingCTA
      title="Start Protecting Your Inbox"
      description="Join thousands of users who are already filtering out spam and AI-generated cold outreach. Try it free for 14 days—no credit card required."
      primaryButtonText="Start Free Trial — No Card Required"
      primaryButtonHref="/login"
      primaryButtonOnClick={handleGetStarted}
      primaryButtonDisabled={isLoading || loading}
      primaryButtonLoading={isLoading || loading}
      badge={{ icon: Sparkles, text: 'Ready to Get Started?' }}
      footerText="✨ No credit card required • 14-day free trial • $5/month after • Cancel anytime"
      footerTextSecondary="We'll remind you before your trial ends. No surprise charges, ever."
      trackingLocation="pricing_final_cta"
      trackingPage="pricing"
    />
  );
}
